const LIVE_STATES = ['LIVE', 'HT', 'ET', 'BREAK', 'PEN_LIVE', 'INPLAY_1ST_HALF', 'INPLAY_2ND_HALF', 'INPLAY_PENALTIES']
const PRE_STATES = ['NS', 'TBA', 'WO', 'ABAN', 'SUSP', 'DELAYED']
const FINISHED_STATES = ['FT', 'AET', 'FT_PEN', 'CANCL', 'POSTP', 'INT']
const LEAGUE_DISPLAY_ALIASES = {
  'pro league': 'Saudi Arabia Pro League',
  'la liga': 'Spain La Liga',
  'serie a': 'Italy Serie A',
  'ligue 1': 'France Ligue 1',
  bundesliga: 'Germany Bundesliga',
  'premier league': 'England Premier League',
}

const STAT_ALIASES = {
  possession: ['ball possession %', 'possession', 'posse de bola'],
  shotsOnTarget: ['shots on target', 'shots on goal', 'chutes no gol', 'remates a baliza'],
  shotsOffTarget: ['shots off target', 'shots off goal', 'chutes fora', 'remates fora'],
  saves: ['saves', 'defesas'],
  dangerousAttacks: ['dangerous attacks', 'ataques perigosos'],
  corners: ['corners', 'corner kicks', 'escanteios', 'cantos'],
  shotsTotal: ['shots total', 'total shots', 'chutes total', 'remates totais'],
  shotsInsideBox: ['shots insidebox', 'shots inside box', 'shots in box', 'shots from inside box', 'shots inside penalty area', 'chutes dentro da area'],
  shotsOutsideBox: ['shots outsidebox', 'shots outside box', 'shots out box', 'shots from outside box', 'shots outside penalty area', 'chutes fora da area'],
  passes: ['passes', 'passes total'],
  bigChancesCreated: ['big chances created', 'big chance created', 'big chances', 'grandes chances criadas'],
  bigChancesMissed: ['big chances missed', 'grandes chances perdidas'],
  duelsWon: ['duels won', 'duelos ganhos'],
  fouls: ['fouls', 'faltas'],
  yellowCards: ['yellowcards', 'yellow cards', 'cartoes amarelos'],
}

const MARKET_STATE_THEME = {
  pre_live: 'neutral',
  home_pressure_building: 'green',
  away_pressure_building: 'green',
  goal_pressure_rising: 'red',
  high_volatility: 'red',
  false_pressure: 'amber',
  balanced_market: 'neutral',
  low_tempo: 'neutral',
  unclear: 'neutral',
}

export const getParticipants = (match) => {
  const participants = Array.isArray(match?.participants) ? match.participants : []
  const home = participants.find((participant) => participant?.meta?.location === 'home')
  const away = participants.find((participant) => participant?.meta?.location === 'away')

  return {
    home,
    away,
    homeName: home?.name || 'Home Team',
    awayName: away?.name || 'Away Team',
  }
}

export const getLeagueLabel = (match) => {
  if (match?.league?.displayName) return match.league.displayName
  const leagueName = match?.league?.name || ''
  const countryName = match?.league?.country?.name || ''
  if (countryName && leagueName) return `${countryName} ${leagueName}`
  const alias = LEAGUE_DISPLAY_ALIASES[String(leagueName || '').toLowerCase().trim()]
  return alias || leagueName || '--'
}

export const getScore = (match) => {
  const { home, away } = getParticipants(match)
  const scores = Array.isArray(match?.scores) ? match.scores : []
  const homeScore =
    scores.find((score) => String(score?.participant_id) === String(home?.id) && score?.description === 'CURRENT')?.score
      ?.goals || 0
  const awayScore =
    scores.find((score) => String(score?.participant_id) === String(away?.id) && score?.description === 'CURRENT')?.score
      ?.goals || 0

  return { home: homeScore, away: awayScore, label: `${homeScore} - ${awayScore}` }
}

const normalizeStatName = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const readStatisticValue = (statistic) => {
  const rawValue = statistic?.data?.value ?? statistic?.value ?? statistic?.data
  if (typeof rawValue === 'string') {
    const cleaned = rawValue.replace('%', '').replace(',', '.')
    const parsed = Number(cleaned)
    return Number.isFinite(parsed) ? parsed : 0
  }
  const parsed = Number(rawValue)
  return Number.isFinite(parsed) ? parsed : 0
}

const findStatMetricKey = (name) => {
  const entries = Object.entries(STAT_ALIASES)
  const exactMatch = entries.find(([, aliases]) =>
    aliases.some((alias) => normalizeStatName(alias) === name)
  )
  if (exactMatch) return exactMatch[0]

  return entries
    .flatMap(([field, aliases]) => aliases.map((alias) => ({ field, alias: normalizeStatName(alias) })))
    .sort((left, right) => right.alias.length - left.alias.length)
    .find(({ alias }) => name.includes(alias))?.field
}

const clamp = (value, min = 0, max = 100) => Math.max(min, Math.min(max, value))
const STAT_PROFILE_KEYS = Object.keys(STAT_ALIASES)
const CUMULATIVE_STAT_KEYS = STAT_PROFILE_KEYS.filter((key) => key !== 'possession')

const makeEmptyStatProfile = () =>
  STAT_PROFILE_KEYS.reduce((accumulator, key) => {
    accumulator[key] = 0
    return accumulator
  }, {})

const hasStatProfileValues = (profile) =>
  Object.entries(profile || {}).some(([key, value]) => key !== 'xg' && Number(value) > 0)

const getStatisticTeamKey = (match, statistic) => {
  const { home, away } = getParticipants(match)
  const participantId = String(statistic?.participant_id || statistic?.participantId || '')
  if (participantId && participantId === String(home?.id)) return 'home'
  if (participantId && participantId === String(away?.id)) return 'away'

  const location = String(statistic?.location || statistic?.meta?.location || '').toLowerCase()
  if (location === 'home' || location === 'away') return location

  return null
}

const getStatisticPeriodOrder = (match, statistic) => {
  const periodOrder = Number(statistic?.period?.sort_order || statistic?.period?.order || 0)
  if (periodOrder) return periodOrder

  const statisticPeriodId = String(statistic?.period_id || statistic?.detailed_period_id || '')
  if (!statisticPeriodId) return null

  const periods = Array.isArray(match?.periods) ? match.periods : []
  const period = periods.find((item) => String(item?.id) === statisticPeriodId)
  return Number(period?.sort_order || 0) || null
}

const getCurrentMinuteValue = (match) => {
  const periods = Array.isArray(match?.periods) ? match.periods : []
  const periodMinute = periods.reduce((highest, period) => {
    const minute = Number(period?.minutes) || 0
    const added = Number(period?.time_added) || 0
    return Math.max(highest, minute + added)
  }, 0)
  if (periodMinute > 0) return periodMinute

  const events = Array.isArray(match?.events) ? match.events : []
  return events.reduce((highest, event) => {
    const minute = Number(event?.minute) || 0
    const added = Number(event?.extra_minute) || 0
    return Math.max(highest, minute + added)
  }, 0)
}

const isStatisticInWindow = (match, statistic, window) => {
  if (window === 'firstHalf') return getStatisticPeriodOrder(match, statistic) === 1
  if (window === 'secondHalf') return getStatisticPeriodOrder(match, statistic) === 2
  return true
}

const aggregateMetricValues = (metricKey, values) => {
  if (!values.length) return 0
  if (metricKey === 'possession') {
    const total = values.reduce((sum, item) => sum + item.value, 0)
    return Math.round((total / values.length) * 10) / 10
  }
  return values.reduce((sum, item) => sum + item.value, 0)
}

const estimateExpectedGoals = (profile) => {
  const shotsTotal = Math.max(0, Number(profile.shotsTotal) || 0)
  let shotsInsideBox = Math.max(0, Number(profile.shotsInsideBox) || 0)
  let shotsOutsideBox = Math.max(0, Number(profile.shotsOutsideBox) || 0)
  const bigChancesCreated = Math.max(0, Number(profile.bigChancesCreated) || 0)

  if (!shotsInsideBox && shotsTotal > 0) {
    shotsInsideBox = Math.max(0, shotsTotal - shotsOutsideBox)
  }
  if (!shotsOutsideBox && shotsInsideBox > 0 && shotsTotal > shotsInsideBox) {
    shotsOutsideBox = Math.max(0, shotsTotal - shotsInsideBox)
  }

  const insideBoxWithoutBigChances = Math.max(0, shotsInsideBox - bigChancesCreated)

  return Number(
    Math.min(
      5,
      bigChancesCreated * 0.38 +
        insideBoxWithoutBigChances * 0.07 +
        shotsOutsideBox * 0.03
    ).toFixed(2)
  )
}

const getHistoryStatRows = (match, window) => {
  const rows = Array.isArray(match?.classicHistory) ? match.classicHistory : []
  const sortedRows = rows
    .filter((row) => Number.isFinite(Number(row?.minuteValue)))
    .sort((left, right) => {
      const leftMinute = Number(left?.minuteValue) || 0
      const rightMinute = Number(right?.minuteValue) || 0
      if (leftMinute !== rightMinute) return leftMinute - rightMinute
      return new Date(left?.capturedAt || 0).getTime() - new Date(right?.capturedAt || 0).getTime()
    })

  if (window === 'firstHalf') return sortedRows.filter((row) => Number(row?.minuteValue) <= 45)
  if (window === 'secondHalf') return sortedRows.filter((row) => Number(row?.minuteValue) > 45)

  if (window === 'last5' || window === 'last10') {
    const latestMinute = Math.max(
      getCurrentMinuteValue(match),
      sortedRows.reduce((highest, row) => Math.max(highest, Number(row?.minuteValue) || 0), 0)
    )
    const windowSize = window === 'last5' ? 5 : 10
    return sortedRows.filter((row) => Number(row?.minuteValue) >= Math.max(0, latestMinute - windowSize))
  }

  return sortedRows
}

const getHistoryBaselineRow = (match, historyRows, window) => {
  const allRows = Array.isArray(match?.classicHistory) ? match.classicHistory : []
  if (window === 'firstHalf') return null
  if (window === 'secondHalf') {
    return [...allRows]
      .filter((row) => Number(row?.minuteValue) <= 45)
      .sort((left, right) => Number(right?.minuteValue) - Number(left?.minuteValue))[0] || historyRows[0] || null
  }
  if (window === 'last5' || window === 'last10') {
    const latestMinute = Math.max(
      getCurrentMinuteValue(match),
      historyRows.reduce((highest, row) => Math.max(highest, Number(row?.minuteValue) || 0), 0)
    )
    const windowSize = window === 'last5' ? 5 : 10
    return [...allRows]
      .filter((row) => Number(row?.minuteValue) <= Math.max(0, latestMinute - windowSize))
      .sort((left, right) => Number(right?.minuteValue) - Number(left?.minuteValue))[0] || null
  }
  return null
}

const buildProfileFromHistory = (match, side, window) => {
  if (!['firstHalf', 'secondHalf', 'last5', 'last10'].includes(window)) return null

  const rows = getHistoryStatRows(match, window)
  if (!rows.length) return null

  const profile = makeEmptyStatProfile()
  const lastRow = rows[rows.length - 1]
  const baselineRow = getHistoryBaselineRow(match, rows, window)
  const lastStats = lastRow?.stats?.[side] || {}
  const baselineStats = baselineRow?.stats?.[side] || {}

  CUMULATIVE_STAT_KEYS.forEach((key) => {
    const lastValue = Number(lastStats[key]) || 0
    const baselineValue = baselineRow ? Number(baselineStats[key]) || 0 : 0
    profile[key] = Math.max(0, lastValue - baselineValue)
  })

  const possessionSamples = rows
    .map((row) => Number(row?.[side]?.possession ?? row?.stats?.[side]?.possession))
    .filter((value) => Number.isFinite(value) && value > 0)
  profile.possession = possessionSamples.length
    ? Math.round((possessionSamples.reduce((sum, value) => sum + value, 0) / possessionSamples.length) * 10) / 10
    : 0

  const lastXg = Number(lastRow?.[side]?.xg ?? lastStats.xg) || 0
  const baselineXg = baselineRow ? Number(baselineRow?.[side]?.xg ?? baselineStats.xg) || 0 : 0
  profile.xg = Math.max(0, Number((lastXg - baselineXg).toFixed(2)))

  return profile
}

export const getTeamStatProfile = (match, side, window = 'total') => {
  const statistics = Array.isArray(match?.statistics) ? match.statistics : []
  const profile = makeEmptyStatProfile()
  const valuesByMetric = STAT_PROFILE_KEYS.reduce((accumulator, key) => {
    accumulator[key] = []
    return accumulator
  }, {})
  const statWindow = ['total', 'firstHalf', 'secondHalf'].includes(window) ? window : 'total'

  statistics.forEach((statistic) => {
    if (getStatisticTeamKey(match, statistic) !== side) return
    if (!isStatisticInWindow(match, statistic, statWindow)) return

    const name = normalizeStatName(statistic?.type?.name || statistic?.name || statistic?.type || statistic?.code)
    const metricKey = findStatMetricKey(name)

    if (!metricKey) return
    valuesByMetric[metricKey].push({
      value: readStatisticValue(statistic),
      periodOrder: getStatisticPeriodOrder(match, statistic),
    })
  })

  Object.entries(valuesByMetric).forEach(([metricKey, values]) => {
    if (!values.length) return
    const fullMatchValues = values.filter((item) => !item.periodOrder)
    profile[metricKey] = statWindow === 'total' && fullMatchValues.length
      ? fullMatchValues[fullMatchValues.length - 1].value
      : aggregateMetricValues(metricKey, values)
  })

  const historyProfile = ['firstHalf', 'secondHalf', 'last5', 'last10'].includes(window)
    ? buildProfileFromHistory(match, side, window)
    : null

  if (window === 'last5' || window === 'last10') {
    const rollingProfile = historyProfile || profile
    rollingProfile.xg = estimateExpectedGoals(rollingProfile)
    return rollingProfile
  }

  if (window === 'firstHalf' || window === 'secondHalf') {
    const mergedProfile = { ...profile }
    if (historyProfile) {
      STAT_PROFILE_KEYS.forEach((key) => {
        if (!Number.isFinite(Number(mergedProfile[key])) || Number(mergedProfile[key]) <= 0) {
          mergedProfile[key] = Number(historyProfile[key]) || 0
        }
      })
    }
    mergedProfile.xg = estimateExpectedGoals(mergedProfile)
    if (hasStatProfileValues(mergedProfile)) return mergedProfile
    return historyProfile || mergedProfile
  }

  profile.xg = estimateExpectedGoals(profile)
  return profile
}

export const getTeamIndexValue = (match, side, indexKey) => {
  const indexes = match?.liveIndexes || {}
  if (indexKey === 'vi') {
    const opponent = side === 'home' ? 'away' : 'home'
    const matchVolatility = Number(indexes?.volatilityIndex?.value) || 0
    const pressure = indexes.pressureIndex || {}
    const control = indexes.controlIndex || {}
    const resistance = indexes.resistanceIndex || {}
    const sidePressure = Number(pressure?.[side]) || 0
    const sideControl = Number(control?.[side]) || 0
    const sideResistance = Number(resistance?.[side]) || 0
    const opponentPressure = Number(pressure?.[opponent]) || 0
    const pressureControlMismatch = Math.abs(sidePressure - sideControl)
    const defensiveStress = Math.max(0, opponentPressure - sideResistance)
    const directTransitionLoad = Math.max(0, sidePressure - sideControl)
    const profile = indexes?.volatilityIndex?.profile || 'mixed_pattern'
    const profileAdjustment =
      profile === `${side}_pattern`
        ? -8
        : profile === `${opponent}_pattern`
          ? 8
          : profile === 'mixed_pattern'
            ? 4
            : 0

    return Math.round(
      clamp(
        matchVolatility * 0.5 +
          pressureControlMismatch * 0.22 +
          defensiveStress * 0.18 +
          directTransitionLoad * 0.1 +
          profileAdjustment
      )
    )
  }
  const sourceByKey = {
    pi: indexes.pressureIndex,
    ci: indexes.controlIndex,
    ri: indexes.resistanceIndex,
  }
  return Number(sourceByKey[indexKey]?.[side]) || 0
}

export const getMinute = (match) => {
  const state = String(match?.state?.state || '').toUpperCase()
  const periods = Array.isArray(match?.periods) ? match.periods : []

  if (PRE_STATES.includes(state) && match?.starting_at_timestamp) {
    return new Date(match.starting_at_timestamp * 1000).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (['HT', 'BREAK', 'HALFTIME'].includes(state)) return 'HT'
  if (['FT', 'FINISHED', 'FULLTIME', 'POSTP', 'AET', 'FT_PEN'].includes(state)) return 'FT'

  if (periods.length) {
    const sortedPeriods = [...periods].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    const currentPeriod = sortedPeriods[sortedPeriods.length - 1]
    const minute = Number(currentPeriod?.minutes) || 0
    const extra = Number(currentPeriod?.time_added) || 0
    if (minute > 0) return extra > 0 ? `${minute}+${extra}'` : `${minute}'`
  }

  if (match?.time?.minute) return `${match.time.minute}'`
  return state || '--'
}

export const isMatchInBucket = (match, bucket) => {
  const state = String(match?.state?.state || '').toUpperCase()
  if (bucket === 'pre') return PRE_STATES.includes(state)
  if (bucket === 'live') return LIVE_STATES.includes(state)
  if (bucket === 'finished') return FINISHED_STATES.includes(state)
  return true
}

export const getMarketStateTheme = (marketState) => MARKET_STATE_THEME[marketState?.state] || 'neutral'

export const deriveLiveContext = (match) => {
  const indexes = match?.liveIndexes || {}
  const pressure = indexes.pressureIndex || { home: 0, away: 0 }
  const control = indexes.controlIndex || { home: 0, away: 0 }
  const resistance = indexes.resistanceIndex || { home: 0, away: 0 }
  const volatility = indexes.volatilityIndex || { value: 0, label: 'low', profile: 'mixed_pattern' }
  const score = getScore(match)
  const periods = Array.isArray(match?.periods) ? match.periods : []
  const minuteValue = periods.reduce((highest, period) => Math.max(highest, Number(period?.minutes) || 0), 0) || Number(match?.time?.minute || 0)

  const pressureHome = Number(pressure.home) || 0
  const pressureAway = Number(pressure.away) || 0
  const controlHome = Number(control.home) || 0
  const controlAway = Number(control.away) || 0
  const resistanceHome = Number(resistance.home) || 0
  const resistanceAway = Number(resistance.away) || 0
  const volatilityValue = Number(volatility.value) || 0
  const scoreHome = Number(score.home) || 0
  const scoreAway = Number(score.away) || 0

  const pressureGap = pressureHome - pressureAway
  const controlGap = controlHome - controlAway
  const resistanceGap = resistanceHome - resistanceAway
  const maxPressure = Math.max(pressureHome, pressureAway)
  const maxControl = Math.max(controlHome, controlAway)
  const maxResistance = Math.max(resistanceHome, resistanceAway)
  const dominantPressure = pressureHome >= pressureAway ? 'home' : 'away'
  const dominantControl = controlHome >= controlAway ? 'home' : 'away'
  const dominantResistance = resistanceHome >= resistanceAway ? 'home' : 'away'
  const alignedPressureControl = dominantPressure === dominantControl
  const scoreDiff = Math.abs(scoreHome - scoreAway)
  const totalGoals = scoreHome + scoreAway
  const opponentResistance = dominantPressure === 'home' ? resistanceAway : resistanceHome

  return {
    pressureHome,
    pressureAway,
    controlHome,
    controlAway,
    resistanceHome,
    resistanceAway,
    volatilityValue,
    volatilityLabel: volatility.label || 'low',
    volatilityProfile: volatility.profile || 'mixed_pattern',
    scoreHome,
    scoreAway,
    minuteValue,
    pressureGap,
    controlGap,
    resistanceGap,
    maxPressure,
    maxControl,
    maxResistance,
    dominantPressure,
    dominantControl,
    dominantResistance,
    alignedPressureControl,
    scoreDiff,
    totalGoals,
    opponentResistance,
  }
}

export const getRisk = (match) => {
  if (isMatchInBucket(match, 'pre')) {
    return { label: '--', tone: 'neutral' }
  }

  const context = deriveLiveContext(match)
  const confidence = Number(match?.marketState?.confidence) || 0

  if (
    context.volatilityValue >= 75 ||
    (context.minuteValue >= 70 && context.scoreDiff <= 1 && context.maxPressure >= 60) ||
    (!context.alignedPressureControl && context.volatilityValue >= 55) ||
    confidence < 0.4
  ) {
    return { label: 'ALTO', tone: 'red' }
  }

  if (
    (context.volatilityValue >= 50 && context.volatilityValue < 75) ||
    (context.maxPressure >= 55 && context.opponentResistance >= 52) ||
    (context.scoreDiff === 1 && context.minuteValue >= 55) ||
    confidence < 0.65
  ) {
    return { label: 'MEDIO', tone: 'amber' }
  }

  return { label: 'BAIXO', tone: 'green' }
}

export const getTriggerLabel = (match) => {
  if (isMatchInBucket(match, 'pre')) return 'Observar'

  const context = deriveLiveContext(match)

  if (
    context.volatilityValue >= 72 ||
    (context.volatilityValue >= 60 && !context.alignedPressureControl)
  ) {
    return 'Volatilidade'
  }

  if (
    context.maxPressure >= 64 &&
    context.alignedPressureControl &&
    context.maxControl >= 54 &&
    context.opponentResistance <= 48 &&
    context.totalGoals <= 3 &&
    (context.scoreDiff <= 1 || context.totalGoals === 0)
  ) {
    return 'Over Goals'
  }

  if (
    context.maxPressure < 42 &&
    context.volatilityValue < 38 &&
    context.maxResistance >= 50 &&
    (context.minuteValue >= 60 || context.scoreDiff >= 1)
  ) {
    return 'Under Goals'
  }

  return 'Observar'
}

export const getHeatValue = (matches) => {
  if (!matches.length) return 0
  const total = matches.reduce((sum, match) => sum + (Number(match?.liveIndexes?.volatilityIndex?.value) || 0), 0)
  return Math.round((total / matches.length / 100) * 10 * 10) / 10
}

export const getPressureLeader = (match) => {
  const { homeName, awayName } = getParticipants(match)
  const pressure = match?.liveIndexes?.pressureIndex || { home: 0, away: 0 }
  return Number(pressure.home) >= Number(pressure.away)
    ? { team: homeName, value: Number(pressure.home) || 0 }
    : { team: awayName, value: Number(pressure.away) || 0 }
}

export const getStatValue = (match, field) => Number(match?.liveIndexes?.[field]) || 0

export const getTopPressureMatches = (matches) =>
  [...matches]
    .map((match) => ({ match, ...getPressureLeader(match) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5)

export const getTriggerDistribution = (matches) => {
  const initial = { volatility: 0, observe: 0, overGoals: 0, underGoals: 0 }
  matches.forEach((match) => {
    const trigger = getTriggerLabel(match)
    if (trigger === 'Volatilidade') initial.volatility += 1
    else if (trigger === 'Over Goals') initial.overGoals += 1
    else if (trigger === 'Under Goals') initial.underGoals += 1
    else initial.observe += 1
  })
  return initial
}

export const getSparklinePoints = (match, width = 58, height = 24) => {
  if (!match?.liveIndexes?.pressureIndex) {
    return `0,12 12,12 24,12 36,12 48,12 58,12`
  }

  const indexes = match?.liveIndexes || {}
  const pressure = indexes.pressureIndex || { home: 0, away: 0 }
  const control = indexes.controlIndex || { home: 0, away: 0 }
  const resistance = indexes.resistanceIndex || { home: 0, away: 0 }
  const volatility = indexes.volatilityIndex || { value: 0 }
  const values = [control.home, pressure.away, resistance.away, pressure.home, resistance.home, 100 - volatility.value].map((value) =>
    Math.max(6, Math.min(96, Number(value) || 0))
  )
  const step = width / Math.max(values.length - 1, 1)
  return values
    .map((value, index) => {
      const x = Math.round(index * step)
      const y = Math.round(height - (value / 100) * height)
      return `${x},${y}`
    })
    .join(' ')
}

export const formatLastUpdate = (value) => {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '--'
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export const formatKickoff = (match) => {
  const timestamp = Number(match?.starting_at_timestamp || 0)
  if (!timestamp) return match?.starting_at || '--'

  return new Date(timestamp * 1000).toLocaleString([], {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
